// Student Promotion: imports and dependencies
import AdminActionIcon from "./AdminActionIcon";
import React, { useEffect, useMemo, useState } from "react";
import { API_BASE_URL } from "../config";
import { useToastMessageState } from "../components/Toast/Toast";
import { LoadingButton } from "../components/Loader/Loader";
import { LoadIcon, PreviewIcon } from "../components/Icons/Icons";
import "../components/Icons/CreateIconButton.css";
import "./StudentPromotion.css";
import { classLevel } from "../utils/classProgression";

// Data types and contracts
type Action = "Promote" | "Repeat" | "Transfer" | "Left" | "PassedOut";
interface Lookup {
  id: number;
  name?: string;
  classId?: number;
  yearStart?: string;
  yearEnd?: string;
}
interface StudentRow {
  enrollmentId: number;
  studentId: number;
  admissionNo: string;
  studentName: string;
  className: string;
  sectionName: string;
  result: string;
  suggestedAction: Action;
  action: Action;
  toClassId: string;
  toSectionId: string;
  rollNumber: string;
}
interface Props {
  selectedSchoolId: number | null;
  initialView?: "promotion" | "history";
}

// Main component and state
const StudentPromotion: React.FC<Props> = ({
  selectedSchoolId,
  initialView = "promotion",
}) => {
  const [view, setView] = useState<"promotion" | "history" | "passedOut">(
    initialView,
  );
  const [sessions, setSessions] = useState<Lookup[]>([]),
    [classes, setClasses] = useState<Lookup[]>([]),
    [sections, setSections] = useState<Lookup[]>([]);
  const [currentSession, setCurrentSession] = useState(""),
    [nextSession, setNextSession] = useState(""),
    [currentClass, setCurrentClass] = useState(""),
    [currentSection, setCurrentSection] = useState("");
  const [defaultClass, setDefaultClass] = useState(""),
    [defaultSection, setDefaultSection] = useState("");
  const [rows, setRows] = useState<StudentRow[]>([]),
    [history, setHistory] = useState<any[]>([]),
    [passedOutStudents, setPassedOutStudents] = useState<any[]>([]);
  const [passedOutSession, setPassedOutSession] = useState(""),
    [passedOutSearch, setPassedOutSearch] = useState("");
  const [loading, setLoading] = useState(false),
    [saving, setSaving] = useState(false),
    [preview, setPreview] = useState(false);
  const [message, setMessage] = useToastMessageState();
  const [options, setOptions] = useState({
    assignFees: true,
    copyTransport: false,
    copyBusRoute: false,
    assignBookKit: false,
    assignUniformKit: false,
    generateRollNumbers: true,
    carryOptionalSettings: true,
  });
  const headers = () => ({
    accept: "application/json",
    Authorization: `Bearer ${localStorage.getItem("token")}`,
    "Content-Type": "application/json",
  });
  const fmtSession = (s: Lookup) =>
    `${new Date(s.yearStart!).getFullYear()}-${new Date(s.yearEnd!).getFullYear()}`;
  useEffect(() => {
    setView(initialView);
  }, [initialView]);
  useEffect(() => {
    if (!selectedSchoolId) return;
    setRows([]);
    Promise.all([
      fetch(
        `${API_BASE_URL}/api/Student/enrollment-info?schoolId=${selectedSchoolId}`,
        { headers: headers() },
      ).then((r) => r.json()),
    ])
      .then(([r]) => {
        setSessions(r.data?.sessions || []);
        setClasses(r.data?.classes || []);
        setSections(r.data?.sections || []);
      })
      .catch(() => setMessage("Unable to load academic options."));
  }, [selectedSchoolId]); // eslint-disable-line
  useEffect(() => {
    if (view === "history" && selectedSchoolId) loadHistory();
    if (view === "passedOut" && selectedSchoolId) loadPassedOut();
  }, [view, selectedSchoolId]); // eslint-disable-line
  const currentLevel = classLevel(
    classes.find((x) => String(x.id) === currentClass)?.name,
  );
  const promotionClasses = classes.filter(
    (x) =>
      currentLevel !== undefined && classLevel(x.name) === currentLevel + 1,
  );
  const nextClassId =
    currentLevel === undefined
      ? ""
      : String(
          classes.find((x) => classLevel(x.name) === currentLevel + 1)?.id ||
            "",
        );
  useEffect(() => {
    setDefaultClass(nextClassId);
    setDefaultSection("");
  }, [currentClass, nextClassId]);
  const currentSections = sections.filter(
    (x) => x.classId === Number(currentClass),
  );
  const destinationSections = sections.filter(
    (x) => x.classId === Number(defaultClass),
  );
  const normalizedPassedOutSearch = passedOutSearch.trim().toLowerCase();
  const filteredPassedOutStudents = passedOutStudents.filter(
    (x) =>
      (!passedOutSession || String(x.sessionId) === passedOutSession) &&
      (!normalizedPassedOutSearch ||
        String(x.studentName)
          .toLowerCase()
          .includes(normalizedPassedOutSearch) ||
        String(x.admissionNo)
          .toLowerCase()
          .includes(normalizedPassedOutSearch)),
  );
  const summary = useMemo(
    () => ({
      loaded: rows.length,
      promote: rows.filter((x) => x.action === "Promote").length,
      repeat: rows.filter((x) => x.action === "Repeat").length,
      transfer: rows.filter((x) => x.action === "Transfer").length,
      left: rows.filter((x) => x.action === "Left").length,
      passedOut: rows.filter((x) => x.action === "PassedOut").length,
    }),
    [rows],
  );
  const loadStudents = async () => {
    if (!currentSession || !currentClass || !currentSection) {
      setMessage("Select the current session, class, and section.");
      return;
    }
    setLoading(true);
    setMessage("");
    try {
      const q = new URLSearchParams({
        schoolId: String(selectedSchoolId),
        currentSessionId: currentSession,
        classId: currentClass,
        sectionId: currentSection,
      });
      const r = await fetch(
        `${API_BASE_URL}/api/student-promotion/students?${q}`,
        { headers: headers() },
      );
      const j = await r.json();
      if (!r.ok) throw new Error(j.message);
      setRows(
        (j.data || []).map((x: any) => ({
          ...x,
          action: "Promote" as Action,
          toClassId: defaultClass,
          toSectionId: defaultSection,
          rollNumber: "",
        })),
      );
      if (!(j.data || []).length)
        setMessage("No active students found for this class and session.");
    } catch (e: any) {
      setMessage(e.message || "Unable to load students.");
    } finally {
      setLoading(false);
    }
  };
  const update = (id: number, patch: Partial<StudentRow>) =>
    setRows((v) =>
      v.map((x) =>
        x.enrollmentId === id
          ? {
              ...x,
              ...patch,
              ...(patch.action === "Repeat"
                ? { toClassId: currentClass, toSectionId: currentSection }
                : patch.action === "Promote"
                  ? { toClassId: defaultClass, toSectionId: defaultSection }
                  : {}),
            }
          : x,
      ),
    );
  const changeDestination = (classId: string, sectionId: string) => {
    if (classId && !promotionClasses.some((c) => String(c.id) === classId)) {
      setMessage("Students can only be promoted to the next class.");
      return;
    }
    setDefaultClass(classId);
    setDefaultSection(sectionId);
    setRows((v) =>
      v.map((x) =>
        x.action === "Promote"
          ? { ...x, toClassId: classId, toSectionId: sectionId }
          : x,
      ),
    );
  };
  const validate = () => {
    if (!rows.length) {
      setMessage("Load students before continuing.");
      return false;
    }
    const invalidPromotion = rows.find(
      (x) =>
        x.action === "Promote" &&
        !promotionClasses.some((c) => String(c.id) === x.toClassId),
    );
    if (invalidPromotion) {
      setMessage(
        `Promotion for ${invalidPromotion.studentName} must be to the next class only. Configure the next class if it is missing.`,
      );
      return false;
    }
    const continuing = rows.filter((x) =>
      ["Promote", "Repeat"].includes(x.action),
    );
    if (continuing.length && !nextSession) {
      setMessage(
        "Select the next session for students being promoted or repeated.",
      );
      return false;
    }
    const invalid = continuing.find((x) => !x.toClassId || !x.toSectionId);
    if (invalid) {
      setMessage(`Choose a destination for ${invalid.studentName}.`);
      return false;
    }
    return true;
  };
  const execute = async () => {
    if (!validate()) return;
    setSaving(true);
    setMessage("");
    try {
      const body = {
        schoolId: selectedSchoolId,
        currentSessionId: Number(currentSession),
        nextSessionId: Number(nextSession),
        ...options,
        students: rows.map((x) => ({
          enrollmentId: x.enrollmentId,
          action: x.action,
          toClassId: x.toClassId ? Number(x.toClassId) : null,
          toSectionId: x.toSectionId ? Number(x.toSectionId) : null,
          rollNumber: x.rollNumber || null,
        })),
      };
      const r = await fetch(`${API_BASE_URL}/api/student-promotion/promote`, {
        method: "POST",
        headers: headers(),
        body: JSON.stringify(body),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.message);
      const warnings = j.data?.warnings?.length
        ? ` Warnings: ${j.data.warnings.join(" ")}`
        : "";
      setMessage(`${j.message}${warnings}`);
      setRows([]);
      setPreview(false);
      loadHistory();
      loadPassedOut();
    } catch (e: any) {
      setMessage(e.message || "Promotion failed.");
    } finally {
      setSaving(false);
    }
  };
  const loadHistory = async () => {
    try {
      const r = await fetch(
        `${API_BASE_URL}/api/student-promotion/history?schoolId=${selectedSchoolId}`,
        { headers: headers() },
      );
      const j = await r.json();
      setHistory(j.data || []);
    } catch {
      setMessage("Unable to load promotion history.");
    }
  };
  const loadPassedOut = async () => {
    try {
      const r = await fetch(
        `${API_BASE_URL}/api/student-promotion/passed-out?schoolId=${selectedSchoolId}`,
        { headers: headers() },
      );
      const j = await r.json();
      setPassedOutStudents(j.data || []);
    } catch {
      setMessage("Unable to load passed-out students.");
    }
  };
  if (!selectedSchoolId)
    return <div className="promotion-card">Please select a school.</div>;
  return (
    <div className="promotion-page">
      <div className="management-tabs">
        <button
          className={`management-tab ${view === "promotion" ? "active" : ""}`}
          onClick={() => setView("promotion")}
        >
          Student Promotion
        </button>
        <button
          className={`management-tab ${view === "history" ? "active" : ""}`}
          onClick={() => setView("history")}
        >
          <AdminActionIcon action="history" />
          Promotion History
        </button>
        <button
          className={`management-tab ${view === "passedOut" ? "active" : ""}`}
          onClick={() => setView("passedOut")}
        >
          Passed Out Students
        </button>
      </div>
      {message && <div className="promotion-message">{message}</div>}
      {view === "history" ? (
        <div className="promotion-card">
          <h2>Promotion History</h2>
          <div className="promotion-table-wrap">
            <table className="promotion-table">
              <thead>
                <tr>
                  <th>Student</th>
                  <th>From Session</th>
                  <th>To Session</th>
                  <th>From Class</th>
                  <th>To Class</th>
                  <th>Type</th>
                  <th>Date</th>
                  <th>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {history.map((x) => (
                  <tr key={x.id}>
                    <td>{x.studentName}</td>
                    <td>{x.fromSession}</td>
                    <td>{x.toSession}</td>
                    <td>{x.fromClass}</td>
                    <td>{x.toClass}</td>
                    <td>
                      <span
                        className={`action-badge ${String(x.type).toLowerCase()}`}
                      >
                        {x.type}
                      </span>
                    </td>
                    <td>{new Date(x.promotionDate).toLocaleDateString()}</td>
                    <td>{x.remarks || "-"}</td>
                  </tr>
                ))}
                {!history.length && (
                  <tr>
                    <td colSpan={8}>No promotion history available.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : view === "passedOut" ? (
        <div className="promotion-card">
          <h2>Passed Out Students</h2>
          <p>
            Completed students remain here for historical records, certificates,
            transcripts, and reporting.
          </p>
          <div className="promotion-grid" style={{ marginBottom: "18px" }}>
            <label>
              Academic Session
              <select
                value={passedOutSession}
                onChange={(e) => setPassedOutSession(e.target.value)}
              >
                <option value="">All Sessions</option>
                {sessions.map((s) => (
                  <option key={s.id} value={s.id}>
                    {fmtSession(s)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Search Student
              <input
                type="search"
                value={passedOutSearch}
                onChange={(e) => setPassedOutSearch(e.target.value)}
                placeholder="Search by name or admission no."
              />
            </label>
          </div>
          <div
            style={{
              marginBottom: "10px",
              color: "#64748b",
              fontSize: "13px",
              fontWeight: 600,
            }}
          >
            Showing {filteredPassedOutStudents.length} of{" "}
            {passedOutStudents.length} passed-out students
          </div>
          <div className="promotion-table-wrap">
            <table className="promotion-table">
              <thead>
                <tr>
                  <th>Admission No</th>
                  <th>Student</th>
                  <th>Final Class</th>
                  <th>Section</th>
                  <th>Session</th>
                  <th>Passed Out Date</th>
                  <th>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {filteredPassedOutStudents.map((x) => (
                  <tr key={x.id}>
                    <td>{x.admissionNo}</td>
                    <td>{x.studentName}</td>
                    <td>{x.className}</td>
                    <td>{x.sectionName}</td>
                    <td>{x.session}</td>
                    <td>{new Date(x.passedOutDate).toLocaleDateString()}</td>
                    <td>{x.remarks || "-"}</td>
                  </tr>
                ))}
                {!filteredPassedOutStudents.length && (
                  <tr>
                    <td colSpan={7}>
                      {passedOutStudents.length
                        ? "No passed-out students match the selected filters."
                        : "No passed-out students found."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <>
          <div className="promotion-card">
            <h2>1. Select source and destination sessions</h2>
            <div className="promotion-grid">
              <label>
                Current Session *
                <select
                  value={currentSession}
                  onChange={(e) => {
                    setCurrentSession(e.target.value);
                    setRows([]);
                  }}
                >
                  <option value="">Select</option>
                  {sessions.map((s) => (
                    <option key={s.id} value={s.id}>
                      {fmtSession(s)}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Next Session (Promote/Repeat only)
                <select
                  value={nextSession}
                  onChange={(e) => setNextSession(e.target.value)}
                >
                  <option value="">Not required for Passed Out</option>
                  {sessions
                    .filter((s) => String(s.id) !== currentSession)
                    .map((s) => (
                      <option key={s.id} value={s.id}>
                        {fmtSession(s)}
                      </option>
                    ))}
                </select>
              </label>
              <label>
                Current Class *
                <select
                  value={currentClass}
                  onChange={(e) => {
                    setCurrentClass(e.target.value);
                    setCurrentSection("");
                    setRows([]);
                  }}
                >
                  <option value="">Select</option>
                  {classes.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Current Section *
                <select
                  value={currentSection}
                  onChange={(e) => setCurrentSection(e.target.value)}
                >
                  <option value="">Select</option>
                  {currentSections.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <LoadingButton
              type="button"
              className="create-icon-button"
              title="Load Students"
              aria-label="Load Students"
              onClick={loadStudents}
              loading={loading}
              loadingText="Loading students..."
            >
              <LoadIcon />
            </LoadingButton>
          </div>
          {rows.length > 0 && (
            <>
              <div className="promotion-card">
                <h2>2. Set common destination</h2>
                <div className="promotion-grid">
                  <label>
                    Move To Class
                    <select
                      value={defaultClass}
                      onChange={(e) => changeDestination(e.target.value, "")}
                    >
                      <option value="">Select next class</option>
                      {promotionClasses.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Move To Section
                    <select
                      value={defaultSection}
                      onChange={(e) =>
                        changeDestination(defaultClass, e.target.value)
                      }
                    >
                      <option value="">Select</option>
                      {destinationSections.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                <p>
                  Students can only be promoted to the next class. If it is
                  missing, configure that class first. Repeat students remain in
                  their current class.
                </p>
              </div>
              <div className="promotion-card">
                <h2>3. Review students and actions</h2>
                <div className="promotion-table-wrap">
                  <table className="promotion-table">
                    <thead>
                      <tr>
                        <th>Admission No</th>
                        <th>Student</th>
                        <th>Current Class</th>
                        <th>Result</th>
                        <th>Action</th>
                        <th>New Class</th>
                        <th>New Section</th>
                        <th>Roll No</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((x) => {
                        const needsDestination = ["Promote", "Repeat"].includes(
                          x.action,
                        );
                        return (
                          <tr key={x.enrollmentId}>
                            <td>{x.admissionNo}</td>
                            <td>{x.studentName}</td>
                            <td>
                              {x.className} / {x.sectionName}
                            </td>
                            <td>{x.result}</td>
                            <td>
                              <select
                                value={x.action}
                                onChange={(e) =>
                                  update(x.enrollmentId, {
                                    action: e.target.value as Action,
                                  })
                                }
                              >
                                {(
                                  [
                                    "Promote",
                                    "Repeat",
                                    "Transfer",
                                    "Left",
                                    "PassedOut",
                                  ] as Action[]
                                ).map((a) => (
                                  <option key={a} value={a}>
                                    {a === "PassedOut" ? "Passed Out" : a}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td>
                              {needsDestination
                                ? classes.find(
                                    (c) => String(c.id) === x.toClassId,
                                  )?.name || "Select common destination"
                                : "-"}
                            </td>
                            <td>
                              <select
                                disabled={!needsDestination}
                                value={x.toSectionId}
                                onChange={(e) =>
                                  update(x.enrollmentId, {
                                    toSectionId: e.target.value,
                                  })
                                }
                              >
                                <option value="">Select</option>
                                {sections
                                  .filter(
                                    (s) => s.classId === Number(x.toClassId),
                                  )
                                  .map((s) => (
                                    <option key={s.id} value={s.id}>
                                      {s.name}
                                    </option>
                                  ))}
                              </select>
                            </td>
                            <td>
                              <input
                                disabled={
                                  !needsDestination ||
                                  options.generateRollNumbers
                                }
                                value={x.rollNumber}
                                onChange={(e) =>
                                  update(x.enrollmentId, {
                                    rollNumber: e.target.value,
                                  })
                                }
                                placeholder={
                                  options.generateRollNumbers
                                    ? "Auto"
                                    : "Roll no"
                                }
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="promotion-card">
                <h2>4. Carry-forward options</h2>
                <div className="promotion-options">
                  {Object.entries({
                    assignFees: "Assign/copy fee assignments",
                    copyTransport: "Copy transport assignment",
                    copyBusRoute: "Copy bus route",
                    assignBookKit: "Assign destination book kit",
                    assignUniformKit: "Assign destination uniform kit",
                    generateRollNumbers: "Generate roll numbers automatically",
                    carryOptionalSettings: "Carry shared profile settings",
                  }).map(([key, label]) => (
                    <label key={key}>
                      <input
                        type="checkbox"
                        checked={(options as any)[key]}
                        onChange={(e) =>
                          setOptions((v) => ({ ...v, [key]: e.target.checked }))
                        }
                      />
                      {label}
                    </label>
                  ))}
                </div>
                <button
                  type="button"
                  className="create-icon-button"
                  title="Preview Promotion"
                  aria-label="Preview Promotion"
                  onClick={() => {
                    if (validate()) setPreview(true);
                  }}
                >
                  <PreviewIcon />
                </button>
              </div>
            </>
          )}
          {preview && (
            <div className="promotion-modal">
              <div className="promotion-preview">
                <h2>Promotion Preview</h2>
                <p>
                  <b>Current:</b>{" "}
                  {sessions.find((x) => String(x.id) === currentSession)
                    ? fmtSession(
                        sessions.find((x) => String(x.id) === currentSession)!,
                      )
                    : "-"}
                </p>
                <p>
                  <b>Next:</b>{" "}
                  {sessions.find((x) => String(x.id) === nextSession)
                    ? fmtSession(
                        sessions.find((x) => String(x.id) === nextSession)!,
                      )
                    : "-"}
                </p>
                <div className="preview-counts">
                  <span>Students: {rows.length}</span>
                  <span>Promote: {summary.promote}</span>
                  <span>Repeat: {summary.repeat}</span>
                  <span>Transfer: {summary.transfer}</span>
                  <span>Left: {summary.left}</span>
                  <span>Passed Out: {summary.passedOut}</span>
                </div>
                <p>
                  This preserves every previous enrollment. Only Promote/Repeat
                  creates a new enrollment; Passed Out moves the student to the
                  historical registry.
                </p>
                <div className="preview-actions">
                  <button className="btn" onClick={() => setPreview(false)}>
                    <AdminActionIcon action="back" />
                    Back
                  </button>
                  <LoadingButton
                    className="btn btn-primary"
                    loading={saving}
                    loadingText="Processing..."
                    onClick={execute}
                  >
                    Confirm & Process
                  </LoadingButton>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
export default StudentPromotion;
